import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { CaretLeft, Clock, User, Calendar, House } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  'basics-of-trading': { en: 'Basics of Trading', it: 'Basi del Trading' },
  'fundamental-analysis': { en: 'Fundamental Analysis', it: 'Analisi Fondamentale' },
  'technical-analysis': { en: 'Technical Analysis', it: 'Analisi Tecnica' },
  'traders-glossary': { en: 'Traders Glossary', it: 'Glossario del Trader' },
  'trader-psychology': { en: 'Trader Psychology', it: 'Psicologia del Trader' },
  'cme-education': { en: 'CME Group Education', it: 'CME Group Education' },
  'articles': { en: 'Articles', it: 'Articoli' },
  'video-webinars': { en: 'Video & Webinars', it: 'Video e Webinar' },
  'fxpro-webinars': { en: 'FxPro Webinars', it: 'Webinar FxPro' },
  'watch-and-learn': { en: 'Watch and Learn', it: 'Guarda e Impara' },
  'open-demo-account': { en: 'Open Demo Account', it: 'Apri Conto Demo' },
};

const categoryLabel = (slug, lang) => CATEGORY_LABELS[slug]?.[lang] || CATEGORY_LABELS[slug]?.en || (slug || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Renders markdown-lite content: ## H2, ### H3, - bullets, > quotes, paragraphs
const renderContent = (content) => {
  if (!content) return null;
  const lines = content.split('\n');
  const blocks = [];
  let buffer = [];
  let bulletBuffer = [];

  const flushParagraph = () => {
    if (buffer.length) {
      blocks.push({ type: 'p', text: buffer.join(' ') });
      buffer = [];
    }
  };
  const flushBullets = () => {
    if (bulletBuffer.length) {
      blocks.push({ type: 'ul', items: bulletBuffer });
      bulletBuffer = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushParagraph(); flushBullets(); continue; }
    if (line.startsWith('## ')) { flushParagraph(); flushBullets(); blocks.push({ type: 'h2', text: line.slice(3) }); continue; }
    if (line.startsWith('### ')) { flushParagraph(); flushBullets(); blocks.push({ type: 'h3', text: line.slice(4) }); continue; }
    if (line.startsWith('> ')) { flushParagraph(); flushBullets(); blocks.push({ type: 'quote', text: line.slice(2) }); continue; }
    if (line.startsWith('- ')) { flushParagraph(); bulletBuffer.push(line.slice(2)); continue; }
    flushBullets();
    buffer.push(line);
  }
  flushParagraph();
  flushBullets();

  return blocks.map((b, i) => {
    if (b.type === 'h2') return <h2 key={i} id={`h-${i}`} className="text-2xl sm:text-3xl font-bold text-brand-primary mt-10 mb-4 scroll-mt-24">{b.text}</h2>;
    if (b.type === 'h3') return <h3 key={i} id={`h-${i}`} className="text-xl font-bold text-slate-800 mt-8 mb-3 scroll-mt-24">{b.text}</h3>;
    if (b.type === 'ul') return <ul key={i} className="list-disc pl-6 space-y-2 my-4 text-slate-700">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
    if (b.type === 'quote') return <blockquote key={i} className="border-l-4 border-brand-accent bg-brand-accent/5 pl-4 py-3 my-6 italic text-slate-700 rounded-r">{b.text}</blockquote>;
    return <p key={i} className="my-4 text-slate-700 leading-relaxed">{b.text}</p>;
  });
};

const buildTOC = (content) => {
  if (!content) return [];
  const toc = [];
  content.split('\n').forEach((line) => {
    const t = line.trim();
    if (t.startsWith('## ')) toc.push({ level: 2, text: t.slice(3) });
    else if (t.startsWith('### ')) toc.push({ level: 3, text: t.slice(4) });
  });
  return toc.slice(0, 12);
};

const KnowledgeArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [siblings, setSiblings] = useState({ prev: null, next: null });

  useEffect(() => {
    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, language]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/knowledge/articles/${slug}`, { params: { language } });
      setArticle(response.data);
      const relResp = await axios.get(`${API}/knowledge/articles`, { params: { category: response.data.category_slug, language } });
      const list = relResp.data || [];
      setRelated(list.filter(a => a.slug !== slug).slice(0, 3));
      const idx = list.findIndex(a => a.slug === slug);
      setSiblings({ prev: idx > 0 ? list[idx - 1] : null, next: idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null });
    } catch (e) {
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  const toc = useMemo(() => buildTOC(article?.content), [article]);
  const readingTime = useMemo(() => {
    if (!article?.content) return 1;
    const words = article.content.split(/\s+/).length;
    return Math.max(1, Math.round(words / 220));
  }, [article]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="loader"></div></div>;
  if (!article) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8">
      <p className="text-slate-600 mb-4">{language === 'it' ? 'Articolo non trovato' : 'Article not found'}</p>
      <button onClick={() => navigate('/knowledge')} className="text-brand-accent hover:underline">← {t('knowledgeHub')}</button>
    </div>
  );

  const catLabel = categoryLabel(article.category_slug, language);
  const dateStr = article.published_at ? new Date(article.published_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-white" data-testid="knowledge-article-page">
      {/* Breadcrumb */}
      <div className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-2 text-xs text-slate-500" data-testid="kh-breadcrumb">
          <Link to="/" className="hover:text-brand-accent flex items-center gap-1"><House size={12} weight="fill" /> {language === 'it' ? 'Home' : 'Home'}</Link>
          <span>/</span>
          <Link to="/knowledge" className="hover:text-brand-accent">{t('knowledgeHub')}</Link>
          <span>/</span>
          <Link to={`/knowledge?cat=${article.category_slug}`} className="hover:text-brand-accent">{catLabel}</Link>
          <span>/</span>
          <span className="text-slate-700 font-medium truncate max-w-[40ch]">{article.title}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 lg:py-14">
        <Link to="/knowledge" className="inline-flex items-center gap-1 text-brand-accent hover:underline mb-6 text-sm" data-testid="kh-back-link">
          <CaretLeft size={16} /> {t('knowledgeHub')}
        </Link>

        {/* Header */}
        <header className="mb-8 lg:mb-12">
          <span className="inline-block text-xs uppercase tracking-wider bg-brand-accent/10 text-brand-accent font-bold px-3 py-1 rounded-full mb-4" data-testid="kh-category-badge">{catLabel}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-primary mb-4 leading-tight" data-testid="kh-article-title">{article.title}</h1>
          {article.excerpt && <p className="text-lg text-slate-600 max-w-3xl mb-6">{article.excerpt}</p>}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><User size={14} weight="fill" /> {article.author || 'WellingtonCDFS Editorial'}</span>
            {dateStr && <span className="flex items-center gap-1.5"><Calendar size={14} weight="fill" /> {dateStr}</span>}
            <span className="flex items-center gap-1.5"><Clock size={14} weight="fill" /> {readingTime} {language === 'it' ? 'min di lettura' : 'min read'}</span>
          </div>
        </header>

        {/* Hero image */}
        {article.image_url && (
          <img src={article.image_url} alt={article.title} className="w-full max-h-[420px] object-cover rounded-2xl mb-10 shadow-sm border border-slate-100" />
        )}

        {article.video_url && (
          <div className="aspect-video mb-10 rounded-2xl overflow-hidden bg-slate-100 shadow-sm border border-slate-100">
            <iframe src={article.video_url} className="w-full h-full" title={article.title} allowFullScreen />
          </div>
        )}

        {/* Content + TOC */}
        <div className="grid lg:grid-cols-[1fr_240px] gap-10 lg:gap-14">
          <article className="min-w-0" data-testid="kh-article-content">
            <div className="prose prose-lg max-w-none">
              {renderContent(article.content)}
            </div>

            {/* Prev / Next */}
            {(siblings.prev || siblings.next) && (
              <div className="mt-14 pt-8 border-t border-slate-200 grid sm:grid-cols-2 gap-4">
                {siblings.prev ? (
                  <Link to={`/knowledge/${siblings.prev.slug}`} className="p-4 rounded-xl border border-slate-200 hover:border-brand-accent hover:shadow-sm transition group" data-testid="kh-prev-article">
                    <div className="text-xs text-slate-500 mb-1">← {language === 'it' ? 'Precedente' : 'Previous'}</div>
                    <div className="font-semibold text-slate-800 group-hover:text-brand-accent">{siblings.prev.title}</div>
                  </Link>
                ) : <div />}
                {siblings.next ? (
                  <Link to={`/knowledge/${siblings.next.slug}`} className="p-4 rounded-xl border border-slate-200 hover:border-brand-accent hover:shadow-sm transition group text-right" data-testid="kh-next-article">
                    <div className="text-xs text-slate-500 mb-1">{language === 'it' ? 'Successivo' : 'Next'} →</div>
                    <div className="font-semibold text-slate-800 group-hover:text-brand-accent">{siblings.next.title}</div>
                  </Link>
                ) : <div />}
              </div>
            )}
          </article>

          {/* TOC sidebar */}
          {toc.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-24 border-l border-slate-200 pl-5" data-testid="kh-toc">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">
                  {language === 'it' ? 'In questo articolo' : 'In this article'}
                </div>
                <ul className="space-y-2 text-sm">
                  {toc.map((item, i) => (
                    <li key={i} className={item.level === 3 ? 'pl-3' : ''}>
                      <a href={`#h-${toc.indexOf(item)}`} className="text-slate-600 hover:text-brand-accent">{item.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-20 pt-12 border-t border-slate-200">
            <h3 className="text-2xl font-bold text-brand-primary mb-6">{language === 'it' ? 'Articoli correlati' : 'Related Articles'}</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map(r => (
                <Link key={r.id} to={`/knowledge/${r.slug}`} className="p-5 bg-slate-50 rounded-xl hover:shadow-md hover:bg-white hover:border-brand-accent border border-transparent transition" data-testid={`kh-related-${r.slug}`}>
                  <div className="text-[10px] uppercase tracking-wider text-brand-accent font-bold mb-2">{categoryLabel(r.category_slug, language)}</div>
                  <div className="font-semibold text-slate-800 line-clamp-2">{r.title}</div>
                  {r.excerpt && <div className="text-xs text-slate-500 mt-2 line-clamp-2">{r.excerpt}</div>}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeArticle;
